import { useEffect, useState } from 'react';
import { InventoryAsset } from '../../../types';
import { Drawer } from '../../Atoms/Drawer';
import { Text } from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { DetailsDisplay } from '../DetailsDrawer/DetailsDisplay';
import {
  fetchInventoryAssetById,
  getInventoryByIdSubject,
  getInventoryLoadingSubject,
  useInventory
} from '../../../store/Inventory';
import { useOutsideInventorySchemasStore } from '../../../store/OutsideInventorySchema';
import { useProductsStore } from '../../../store/Product';

export interface InventoryRecordDrawerProps {
  assetId: string;
  onClose: () => void;
  testIdPrefix: string;
}

export function InventoryRecordDrawer({ assetId, onClose, testIdPrefix }: InventoryRecordDrawerProps) {
  const [asset, setAsset] = useState<InventoryAsset | undefined>(undefined);
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);
  const [fetchAttempted, setFetchAttempted] = useState<boolean>(false);
  const { fetchInventoryById } = useInventory();
  const { fetchProducts } = useProductsStore();
  const { fetchOutsideInventorySchemas } = useOutsideInventorySchemasStore();

  useEffect(() => {
    fetchProducts();
    fetchOutsideInventorySchemas();
  }, []);

  useEffect(() => {
    const sub = getInventoryByIdSubject().subscribe((loaded) => {
      if (loaded && loaded.id === assetId) setAsset(loaded);
    });
    const loadingSub = getInventoryLoadingSubject().subscribe(setInventoryLoading);
    return () => {
      sub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, [assetId]);

  useEffect(() => {
    setAsset(undefined);
    setFetchAttempted(false);
    fetchInventoryById(assetId).finally(() => setFetchAttempted(true));
  }, [assetId]);

  const refresh = async () => {
    if ((await fetchInventoryAssetById(assetId)) === 'gone') {
      setAsset(undefined);
      setFetchAttempted(true);
    }
  };

  const loadFailed = fetchAttempted && !inventoryLoading && !asset;

  return (
    <Drawer
      data-testid={`${testIdPrefix}-details-drawer`}
      show
      setShow={onClose}
      title="Inventory Details"
      subtitle={asset && <Text size="lg">{asset.address || asset.id}</Text>}
      componentName="inventory"
      data={asset || null}
      onRefresh={() => void refresh()}>
      {asset && (
        <DetailsDisplay
          data={asset}
          componentName="inventory"
          setShow={onClose}
        />
      )}
      {!asset && !loadFailed && (
        <div className="mt-10">
          <ComponentSpinner />
        </div>
      )}
      {loadFailed && (
        <div
          data-testid={`${testIdPrefix}-details-error`}
          className="mt-10 px-4 text-center">
          <Text size="sm">
            The inventory record could not be loaded. It may be hidden from view in the portal or unavailable for the
            currently selected account.
          </Text>
        </div>
      )}
    </Drawer>
  );
}
